import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'

declare const gon: any

export interface StreamWithActivity {
    id: string
    name: string
    last_activity?: string
    quest_count?: number
    public?: boolean
    // Add other ITeam properties as needed
}

interface GetStreamsResponse {
    teams: StreamWithActivity[]
    has_more: boolean
    next_page?: number
    total_count: number
}

const fetchStreams = async ({ pageParam = 1 }): Promise<GetStreamsResponse> => {
    const response = await fetch(`${window.knovApiUrl}/api/v1/streams?page=${pageParam}`, {
        headers: {
            ...api.commonHeaders(),
            ...accessTokens(),
            'X-CSRF-Token': gon.formToken,
        },
        credentials: 'same-origin',
    })

    if (!response.ok) {
        throw new Error('Failed to fetch streams')
    }

    const data = await response.json()
    return {
        teams: data.teams || [],
        has_more: data.has_more || false,
        next_page: data.next_page,
        total_count: data.total_count || 0,
    }
}

export const useGetStreams = (enabled = true) => {
    const query = useInfiniteQuery({
        queryKey: ['streams'],
        queryFn: fetchStreams,
        enabled,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        structuralSharing: false,
        networkMode: 'always',
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => {
            const fetchedAmount = pages.reduce((acc, page) => acc + page.teams.length, 0)
            return fetchedAmount < lastPage.total_count ? pages.length + 1 : undefined
        },
    })

    // Flatten all pages into a single array of streams
    const streams = query.data?.pages.flatMap(page => page.teams) || []

    return {
        streams,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
        refetch: query.refetch,
        totalStreams: query.data?.pages[0]?.total_count || 0,
    }
}
